export type NewsType = "news" | "interview" | "event";

export interface NewsBlock {
  heading?: string;
  paragraphs: string[];
  image?: string;
  caption?: string;
}

export interface NewsItem {
  id: string;
  title: string;
  type: NewsType;
  date: string;
  summary: string;
  cover: string;
  tags: string[];
  relatedWorkId?: string;
  content: NewsBlock[];
}

export const newsTypes: { value: NewsType; label: string }[] = [
  { value: "news", label: "新闻" },
  { value: "interview", label: "访谈" },
  { value: "event", label: "活动报道" },
];

export const newsList: NewsItem[] = [
  {
    id: "night-train-premiere",
    title: "《夜行列车》东京首映礼落幕，翔田千里谈「最安静的一次演出」",
    type: "news",
    date: "2024-11-12",
    summary:
      "电影《夜行列车》于东京举行首映礼，翔田千里与导演黑泽和树一同出席。她在映后分享中表示，这个角色让她第一次学会「用沉默表演」。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20amber%20spotlights%20over%20a%20dark%20cinema%20stage%20with%20deep%20navy%20curtains%20and%20a%20single%20vintage%20microphone%20stand%2C%20elegant%20premiere%20atmosphere%2C%20soft%20haze%2C%20cinematic%20editorial%20photography%2C%20no%20text&width=800&height=500&seq=news-premiere-night-train&orientation=landscape",
    tags: ["夜行列车", "首映礼", "电影"],
    relatedWorkId: "night-train",
    content: [
      {
        paragraphs: [
          "11 月 10 日晚，电影《夜行列车》在东京丸之内举行首映礼。导演黑泽和树、主演翔田千里以及主要演员阵容出席红毯。现场以片中标志性的站台灯箱作为主视觉，观众席两侧悬挂着从影片中截取的雨夜画面。",
          "影片放映结束后，全场安静了将近十秒才响起掌声。黑泽和树在映后表示，这样的反应正是他想要的：「这不是一部需要立刻讨论的电影，它更像是让人独自走一段夜路。」",
        ],
        image:
          "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20an%20elegant%20movie%20theatre%20auditorium%20with%20warm%20amber%20stage%20light%20and%20deep%20navy%20seats%2C%20empty%20before%20screening%2C%20soft%20haze%2C%20premium%20editorial%20photography%2C%20no%20people%2C%20no%20text&width=1000&height=640&seq=news-premiere-hall&orientation=landscape",
        caption: "首映礼现场以片中站台灯箱作为主视觉",
      },
      {
        heading: "关于安静的表演",
        paragraphs: [
          "翔田千里在片中饰演的水野澪几乎没有大段独白，情绪的推进全部依靠眼神与呼吸。她提到，为了找到人物的节奏，自己在开拍前连续两周每天搭乘末班电车，「不说话，只观察」。",
          "「这个角色最难的地方不是哭戏，而是让别人相信她一直在想事情。」她笑着说，「所以我学会了在镜头前什么都不做。」",
        ],
      },
      {
        heading: "后续上映计划",
        paragraphs: [
          "《夜行列车》将于 11 月 22 日起在日本全国公映，并计划于次年春季在部分亚洲城市进行展映。影片已确认入围本年度亚洲电影展的「当代视野」单元。",
        ],
      },
    ],
  },
  {
    id: "white-corridor-season-two",
    title: "《白色回廊》确认制作第二季，原班主创回归",
    type: "news",
    date: "2024-10-03",
    summary:
      "剧集《白色回廊》官方宣布第二季正式启动，翔田千里将继续出演相泽玲，故事将把视角从重症监护室转向医院更深处。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20bright%20modern%20hospital%20corridor%20with%20deep%20navy%20door%20frames%20and%20warm%20amber%20evening%20light%20through%20tall%20windows%2C%20clean%20minimal%20architecture%2C%20quiet%20tension%2C%20editorial%20photography%2C%20no%20people%2C%20no%20text&width=800&height=500&seq=news-white-corridor-s2&orientation=landscape",
    tags: ["白色回廊", "剧集", "续订"],
    relatedWorkId: "white-corridor",
    content: [
      {
        paragraphs: [
          "10 月 2 日，剧集《白色回廊》制作方在官方渠道确认第二季已进入前期筹备阶段。第一季的编剧团队与导演森田圭均将回归，翔田千里也已确认继续出演主角相泽玲。",
          "第一季共 10 集，以地方医院重症监护室为舞台，用近乎纪实的方式呈现医护人员在高强度环境中的判断与犹豫。播出期间，关于「医疗剧是否需要如此克制」的讨论持续了数周。",
        ],
      },
      {
        heading: "更深的走廊",
        paragraphs: [
          "据制作方透露，第二季的叙事重心将从重症监护室扩展到医院的行政区域与地下层，「回廊会变得更长，灯光也会更少」。森田圭表示，他希望继续用缓慢的镜头去换取观众对细节的耐心。",
          "翔田千里在简短回应中提到，相泽玲在第二季会面对更多「没有标准答案」的选择。她已经开始跟随医疗顾问进行新一轮的现场观察，为角色做准备。",
        ],
        image:
          "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20an%20empty%20hospital%20stairwell%20with%20cool%20light%20from%20above%20and%20warm%20amber%20emergency%20lamp%2C%20deep%20navy%20shadows%2C%20restrained%20clinical%20mood%2C%20wide%20composition%2C%20no%20people%2C%20no%20text&width=1000&height=640&seq=news-white-corridor-still&orientation=landscape",
        caption: "第二季将把舞台延伸到医院的更深处",
      },
      {
        heading: "预计播出时间",
        paragraphs: [
          "第二季共 8 集，计划于次年秋季播出。第一季完整版目前仍可在主要流媒体平台点播，本站不对播放渠道提供具体指引。",
        ],
      },
    ],
  },
  {
    id: "interview-on-silence",
    title: "访谈｜翔田千里：我不太相信「爆发力」这回事",
    type: "interview",
    date: "2024-08-19",
    summary:
      "在一小时的访谈中，翔田千里聊了她对表演节奏的理解、对剧本的挑剔，以及为什么她越来越喜欢在片场「等一等」。",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20portrait%20scene%20of%20an%20empty%20quiet%20interview%20room%20with%20two%20wooden%20chairs%2C%20warm%20amber%20window%20light%2C%20deep%20navy%20blue%20wall%20shadows%2C%20minimal%20elegant%20composition%2C%20cinematic%20photography%2C%20no%20people%2C%20no%20text&width=800&height=500&seq=news-interview-silence&orientation=landscape",
    tags: ["访谈", "表演", "夜行列车"],
    relatedWorkId: "letters-of-the-tide",
    content: [
      {
        paragraphs: [
          "采访约在下午三点，一间没有窗的录音室。翔田千里提前十分钟到，自己带了一杯温水。她说最近在做一件很无聊的事：重看自己十年前的作品，「看到尴尬的地方就快进」。",
        ],
      },
      {
        heading: "关于节奏",
        paragraphs: [
          "「我不太相信爆发力这回事。」她说，「爆发力是观众能看见的，但角色真正的决定往往发生在爆发之前。那个瞬间如果没有铺垫，爆发就只是音量。」",
          "她把表演比作剪辑：「演员提供素材，节奏是后期的事。但如果你在片场就把所有情绪用尽，剪辑师就没有东西可以剪了。」",
        ],
      },
      {
        heading: "关于挑选剧本",
        paragraphs: [
          "近三年她的作品数量明显减少，平均每年只接一到两部。她承认这是有意的：「我需要时间忘掉上一个角色，否则我会把她的习惯带到下一个人身上。」",
          "「我看剧本的时候会先找这个人物独处的那几场戏。如果那几场写得不好，其他部分再精彩我也不会接。」",
        ],
        image:
          "https://readdy.ai/api/search-image?query=Cinematic%20still%20life%20of%20a%20script%20with%20handwritten%20notes%2C%20a%20glass%20of%20water%20and%20a%20small%20desk%20lamp%20on%20a%20wooden%20table%2C%20warm%20amber%20light%2C%20deep%20navy%20background%2C%20quiet%20editorial%20mood%2C%20shallow%20depth%20of%20field%2C%20no%20text&width=1000&height=640&seq=news-interview-desk&orientation=landscape",
        caption: "她习惯在剧本空白处写下人物的呼吸节奏",
      },
      {
        heading: "关于未来",
        paragraphs: [
          "被问到是否考虑幕后工作，她没有否认：「我对剪辑室很好奇，那是一个可以重新理解表演的地方。」但她强调，短期内仍然只想把演员这件事做好。",
        ],
      },
    ],
  },
  {
    id: "tide-letters-anniversary",
    title: "《潮汐之信》上映一周年，特别放映会在镰仓举行",
    type: "event",
    date: "2024-09-16",
    summary:
      "为纪念《潮汐之信》上映一周年，片方在镰仓举办特别放映会。翔田千里到场与观众一同重温影片，并分享拍摄期间的海岛生活。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20amber%20evening%20light%20over%20a%20small%20seaside%20town%20harbor%20with%20wooden%20boats%20and%20lanterns%2C%20deep%20navy%20blue%20dusk%20sky%2C%20gentle%20nostalgic%20atmosphere%2C%20cinematic%20wide%20editorial%20photography%2C%20no%20people%2C%20no%20text&width=800&height=500&seq=news-tide-anniversary&orientation=landscape",
    tags: ["潮汐之信", "放映会", "活动"],
    relatedWorkId: "letters-of-the-tide",
    content: [
      {
        paragraphs: [
          "9 月 14 日，《潮汐之信》上映一周年特别放映会在神奈川县镰仓市举行。放映场地选在一间由旧仓库改造的小剧场，现场仅容纳一百余名观众。",
          "翔田千里在放映前简短致辞。她提到，拍摄期间剧组住在岛上的一所旧小学里，「每天早上被海浪声叫醒，晚上八点就困了」。",
        ],
        image:
          "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20small%20converted%20warehouse%20theatre%20with%20warm%20amber%20string%20lights%20and%20wooden%20benches%2C%20deep%20navy%20shadowed%20rafters%2C%20intimate%20seaside%20screening%20atmosphere%2C%20no%20people%2C%20no%20text&width=1000&height=640&seq=news-tide-theater&orientation=landscape",
        caption: "放映会场地由旧仓库改造而成",
      },
      {
        heading: "观众提问",
        paragraphs: [
          "有观众问到片中那场写信的长镜头，翔田千里透露，那一条其实拍了十七条：「导演要的不是眼泪，是手在抖但字还能写下去。第十七条的时候我真的写不下去了，他就喊过。」",
        ],
      },
      {
        heading: "关于修复版",
        paragraphs: [
          "片方同时宣布，《潮汐之信》的 4K 修复版正在制作中，预计于次年春天在日本部分艺术影院重映。",
        ],
      },
    ],
  },
  {
    id: "silent-testimony-award",
    title: "《静默的证词》获年度剧集提名，翔田千里入围最佳女主角",
    type: "news",
    date: "2024-06-28",
    summary:
      "年度电视剧评选公布入围名单，《静默的证词》获得四项提名，翔田千里凭桐生遥一角入围最佳女主角。",
    cover:
      "https://readdy.ai/api/search-image?query=Elegant%20close%20up%20of%20a%20blank%20gold%20award%20envelope%20on%20a%20deep%20navy%20velvet%20surface%20with%20warm%20amber%20spotlight%2C%20premium%20cinematic%20editorial%20still%20life%2C%20shallow%20depth%20of%20field%2C%20no%20text&width=800&height=500&seq=news-award-nomination&orientation=landscape",
    tags: ["静默的证词", "提名", "奖项"],
    relatedWorkId: "silent-testimony",
    content: [
      {
        paragraphs: [
          "6 月 27 日，年度电视剧评选公布入围名单。《静默的证词》共获得最佳剧集、最佳女主角、最佳编剧与最佳摄影四项提名。",
          "翔田千里在剧中饰演专攻无声证人的辩护律师桐生遥。剧集以大量法庭长镜头与手语交流场景著称，播出时曾引发关于「影像如何呈现沉默」的讨论。",
        ],
      },
      {
        heading: "剧组回应",
        paragraphs: [
          "编剧团队在社交平台表示，这个故事的起点是一次旁听经历：「我们发现最有力的陈述，往往来自说得最少的人。」",
          "翔田千里方面则通过经纪公司回应，感谢剧组与观众，「提名属于整个团队」。",
        ],
      },
      {
        heading: "颁奖时间",
        paragraphs: [
          "颁奖礼预计于 9 月举行。本站将持续跟进相关动态。",
        ],
      },
    ],
  },
  {
    id: "kirishima-restoration",
    title: "《雾岛来信》数字修复版上线，新增幕后花絮",
    type: "news",
    date: "2024-04-11",
    summary:
      "上映两年的《雾岛来信》推出数字修复版，除画面调色重制外，还附带一段二十分钟的幕后纪录短片。",
    cover:
      "https://readdy.ai/api/search-image?query=Misty%20green%20mountain%20valley%20at%20dawn%20with%20warm%20amber%20sunlight%20breaking%20through%20fog%2C%20deep%20navy%20pine%20forest%20silhouettes%2C%20serene%20cinematic%20wide%20landscape%20photography%2C%20no%20people%2C%20no%20text&width=800&height=500&seq=news-kirishima-restore&orientation=landscape",
    tags: ["雾岛来信", "修复版", "幕后"],
    relatedWorkId: "letters-from-kirishima",
    content: [
      {
        paragraphs: [
          "片方于 4 月 10 日推出《雾岛来信》数字修复版。此次修复主要针对原片中大量雾气场景的层次进行重新调色，保留胶片颗粒的同时提升了暗部细节。",
          "修复工作由原摄影指导监修，历时约五个月。团队表示，最大的困难是「不让雾变得更干净」——过度的数字降噪会让山景失去原本的呼吸感。",
        ],
      },
      {
        heading: "幕后纪录短片",
        paragraphs: [
          "随修复版一同上线的还有一段二十分钟的幕后纪录短片，记录了剧组在雾岛为期三周的拍摄过程，其中包括翔田千里学习使用中画幅相机的片段。",
        ],
        image:
          "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20vintage%20medium%20format%20camera%20resting%20on%20a%20mossy%20wooden%20fence%20with%20misty%20green%20forest%20behind%2C%20warm%20amber%20morning%20light%2C%20deep%20navy%20shadows%2C%20quiet%20editorial%20mood%2C%20no%20text&width=1000&height=640&seq=news-kirishima-camera&orientation=landscape",
        caption: "幕后短片中出现的相机，是片中角色的重要道具",
      },
    ],
  },
  {
    id: "profile-photobook",
    title: "翔田千里首本影像随笔集《在场景之外》确定出版",
    type: "news",
    date: "2024-02-20",
    summary:
      "收录翔田千里近五年片场随笔与黑白影像的《在场景之外》确定于春季出版，部分内容为首次公开。",
    cover:
      "https://readdy.ai/api/search-image?query=Elegant%20still%20life%20of%20an%20open%20hardcover%20photo%20book%20with%20blank%20matte%20pages%20on%20a%20dark%20wooden%20table%2C%20warm%20amber%20window%20light%2C%20deep%20navy%20shadows%2C%20minimal%20editorial%20photography%2C%20no%20text&width=800&height=500&seq=news-photobook&orientation=landscape",
    tags: ["出版", "影像随笔", "动态"],
    content: [
      {
        paragraphs: [
          "出版社于 2 月 19 日宣布，翔田千里的首本影像随笔集《在场景之外》将于春季出版。全书收录她在过去五年间写下的片场笔记，以及约一百二十幅黑白照片。",
          "据编辑介绍，书中的照片大多拍摄于拍摄间隙，「没有一张是剧照」。文字部分则以短句为主，记录天气、盒饭、等待的时间。",
        ],
      },
      {
        heading: "关于书名",
        paragraphs: [
          "翔田千里在序言中写道：「观众看到的是场景之内，但演员记得的往往是场景之外的那几分钟。」",
        ],
        image:
          "https://readdy.ai/api/search-image?query=Cinematic%20overhead%20still%20life%20of%20handwritten%20notes%20and%20black%20and%20white%20photo%20prints%20scattered%20on%20a%20wooden%20desk%20with%20warm%20amber%20lamp%20light%2C%20deep%20navy%20shadows%2C%20quiet%20intimate%20mood%2C%20no%20text&width=1000&height=640&seq=news-photobook-desk&orientation=landscape",
        caption: "书中收录的片场笔记手稿",
      },
      {
        heading: "发行信息",
        paragraphs: [
          "首印版本将附带一张双面印刷的片场明信片，具体发行日期与渠道以出版社公告为准。",
        ],
      },
    ],
  },
  {
    id: "tokyo-twelve-screening",
    title: "《东京十二时》午夜场重映，导演出席映后交流",
    type: "event",
    date: "2023-12-05",
    summary:
      "《东京十二时》在新宿一家老影院举行午夜场重映，导演高梨悠到场与观众交流，谈到影片的声音设计。",
    cover:
      "https://readdy.ai/api/search-image?query=Neon%20lit%20Tokyo%20backstreet%20at%20midnight%20with%20warm%20amber%20signage%20and%20wet%20asphalt%20reflections%2C%20deep%20navy%20blue%20night%20sky%2C%20cinematic%20urban%20editorial%20photography%2C%20no%20people%2C%20no%20text&width=800&height=500&seq=news-tokyo-midnight&orientation=landscape",
    tags: ["东京十二时", "重映", "活动"],
    relatedWorkId: "tokyo-twelve-hours",
    content: [
      {
        paragraphs: [
          "12 月 2 日深夜，《东京十二时》在新宿一家开业逾六十年的影院举行午夜场重映。放映结束后已近凌晨三点，仍有大半观众留在座位上参与交流。",
        ],
      },
      {
        heading: "关于声音",
        paragraphs: [
          "导演高梨悠提到，全片最重要的不是画面而是声音：「我们把对白压得很低，让环境音变成主角。观众如果错过一句台词，其实是设计的一部分。」",
          "他同时透露，片中电台直播间的场景是在真实的深夜节目录音棚拍摄的，剧组只能在节目结束后进场，每晚只有四小时。",
        ],
      },
      {
        heading: "观众反响",
        paragraphs: [
          "有观众表示，这是自己第三次在影院看这部片，「每次注意到的是不同的人」。高梨悠回应说，这正是他希望留下的余味。",
        ],
      },
    ],
  },
  {
    id: "summer-chapter-retrospective",
    title: "《夏日终章》入选青年影像回顾展，翔田千里寄语音频公开",
    type: "news",
    date: "2023-06-30",
    summary:
      "青年影像回顾展公布片单，《夏日终章》入选「成长之间」单元。翔田千里为本次展映录制了一段寄语音频。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20amber%20sunlight%20streaming%20through%20dusty%20windows%20of%20an%20abandoned%20wooden%20auditorium%20with%20empty%20chairs%2C%20deep%20navy%20shadows%2C%20nostalgic%20cinematic%20wide%20photography%2C%20no%20people%2C%20no%20text&width=800&height=500&seq=news-summer-retro&orientation=landscape",
    tags: ["夏日终章", "展映", "回顾展"],
    relatedWorkId: "last-chapter-of-summer",
    content: [
      {
        paragraphs: [
          "青年影像回顾展于 6 月 29 日公布完整片单，《夏日终章》入选「成长之间」单元，将与另外六部作品共同展映。",
          "主办方同时公开了翔田千里为本次展映录制的寄语音频。她在音频中提到，拍摄时自己与片中演员年龄相近，「很多情绪不需要演，只需要想起来」。",
        ],
      },
      {
        heading: "展映安排",
        paragraphs: [
          "该单元将于 7 月中旬在三个城市巡展，每场放映后设有青年导演对谈。具体场次以主办方公告为准。",
        ],
      },
    ],
  },
];

export const getNewsById = (id: string): NewsItem | undefined =>
  newsList.find((item) => item.id === id);

export const newsTypeLabel = (type: NewsType): string => {
  const found = newsTypes.find((item) => item.value === type);
  return found ? found.label : "资讯";
};